/**
 * Convierte la respuesta de la API de releases (backoffice) al formato
 * VersionInfo/VersionsArray que usa About (Version history).
 */
import type { VersionsArray } from './types';
import type { VersionStatistics } from './types';
import type { ApiReleaseDetail, ApiChangelogEntry } from '../../services/releasesApiService';

/**
 * Formato de texto del changelog (p:: párrafo, sub:: sub-bullet, sino bullet).
 * Reconoce los mismos prefijos que el backoffice: -, --, •, *, >.
 * No modifica enlaces [texto](url); Changelog.parseRichText los convierte en <a>.
 */
function detectFormatting(text: string): string {
  let clean = text.trim();
  const subBulletWithDashes = clean.startsWith('--') && clean.length > 2 && /[\s:]/.test(clean[2]);
  const subBulletWithAngle = clean.startsWith('>');
  const isSubBullet = subBulletWithDashes || subBulletWithAngle;
  const bulletWithDash = clean.startsWith('-') && !subBulletWithDashes;
  const bulletWithDot = clean.startsWith('•');
  const bulletWithStar = clean.startsWith('*') && (clean.length === 1 || clean[1] === ' ');
  const isBullet = bulletWithDash || bulletWithDot || bulletWithStar;

  if (isSubBullet) {
    if (subBulletWithDashes) clean = clean.slice(2).trim();
    else if (subBulletWithAngle) clean = clean.slice(1).trim();
  } else if (isBullet) {
    if (bulletWithStar) clean = clean.slice(1).trim(); // "* " or "*"
    else clean = clean.slice(1).trim(); // "•" or "-"
  }

  clean = clean.replace(/\*([^*]+)\*/g, '**$1**');
  clean = clean.replace(/10\^(-?\d+)/g, '10<sup>$1</sup>');

  if (isSubBullet) return `sub::${clean}`;
  if (isBullet) return clean;
  return `p::${clean}`;
}

function cleanChangelogText(raw: string): string {
  if (!raw || typeof raw !== 'string') return '';
  let clean = raw.trim();
  while (
    (clean.startsWith('"') && clean.endsWith('"')) ||
    (clean.startsWith("'") && clean.endsWith("'"))
  ) {
    clean = clean.slice(1, -1).trim();
  }
  return clean.replace(/""/g, '"');
}

/**
 * Convierte statistics de la API (array {dataset, count}) a VersionStatistics (objeto).
 */
function apiStatisticsToVersionStatistics(
  statistics: ApiReleaseDetail['statistics']
): VersionStatistics {
  const obj: Record<string, number> = {};
  if (statistics) {
    for (const { dataset, count } of statistics) {
      const key = dataset.trim();
      const num = parseInt(String(count), 10);
      if (!Number.isNaN(num)) obj[key] = num;
    }
  }
  return obj as VersionStatistics;
}

/**
 * Convierte changelog de la API (array de {Section, Text, releaseDate}) a Record<string, string[]>.
 */
function apiChangelogToRecord(changelog: ApiChangelogEntry[] | null): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (!changelog) return result;
  for (const entry of changelog) {
    const section = cleanChangelogText(entry.Section || '');
    let text = cleanChangelogText(entry.Text || '');
    if (!section || !text) continue;
    if (!result[section]) result[section] = [];
    result[section].push(detectFormatting(text));
  }
  return result;
}

/**
 * Formatea versión YYYY-MM-DD como "December 15th 2025" para el título.
 */
function formatVersionAsDate(version: string): string {
  const trimmed = (version || '').trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!isoMatch) return trimmed;
  const [, y, m, d] = isoMatch;
  const monthIndex = parseInt(m, 10) - 1;
  const day = parseInt(d, 10);
  const ordinals = ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const suffix = day >= 10 && day <= 20 ? 'th' : ordinals[day % 10];
  return `${months[monthIndex]} ${day}${suffix} ${y}`;
}

/**
 * Formatea una fecha en formato AAAA-MM-DD a "December 15th 2025".
 */
function formatDateDisplay(dateOrVersion: string): string {
  const trimmed = (dateOrVersion || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return formatVersionAsDate(trimmed);
  return trimmed;
}

/**
 * Obtiene releaseDate del primer entry del changelog o valor por defecto.
 * La fecha (del changelog o la versión) se muestra siempre en formato "Month DDth YYYY" si es AAAA-MM-DD.
 */
function getReleaseDateFromApi(changelog: ApiChangelogEntry[] | null, version: string): string {
  const first = changelog?.[0];
  const date = first?.releaseDate?.trim();
  if (date && date.toLowerCase() !== 'unknown release date') {
    return formatDateDisplay(date);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test((version || '').trim())) {
    return formatVersionAsDate(version);
  }
  return `Release ${version}`;
}

/**
 * Mapea un release devuelto por la API a VersionsArray (formato About).
 */
export function apiReleaseToVersionsArray(detail: ApiReleaseDetail): VersionsArray {
  const releaseDate = getReleaseDateFromApi(detail.changelog, detail.version);
  const statistics = apiStatisticsToVersionStatistics(detail.statistics);
  const changelog = apiChangelogToRecord(detail.changelog);

  return {
    version: detail.version,
    releaseDate,
    statistics,
    changelog,
  };
}
